import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/lang";
import { EASE, Eyebrow, Reveal } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Waitlist = ({ app }) => {
  const { t, lang } = useLang();
  const w = t.waitlist;
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      toast.error(w.invalid);
      return;
    }
    setState("sending");
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email: email.trim(), app: app.slug, lang });
      setState(data.status === "already_confirmed" ? "already" : "pending");
    } catch {
      setState("idle");
      toast.error(w.error);
    }
  };

  return (
    <Reveal
      data-testid="waitlist-section"
      className="relative mt-16 overflow-hidden rounded-[32px] border border-white/10 p-8 sm:p-12"
      style={{ background: `radial-gradient(90% 140% at 0% 0%, ${app.accent}26, transparent 60%), radial-gradient(70% 120% at 100% 100%, ${app.accent2}1f, transparent 60%)` }}
    >
      <div className="grid items-center gap-10 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Eyebrow>{w.eyebrow}</Eyebrow>
          <p data-testid="waitlist-title" className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-5xl">{w.title(app.name)}</p>
          <p className="mt-4 max-w-md text-white/60">{w.sub}</p>
        </div>
        <div className="lg:col-span-6">
          <AnimatePresence mode="wait">
            {state === "pending" || state === "already" ? (
              <motion.div key="done" data-testid="waitlist-success" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="flex items-start gap-4">
                <motion.span initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 18 }} className="grid h-12 w-12 shrink-0 place-items-center rounded-full text-[#05070f]" style={{ background: `linear-gradient(135deg, ${app.accent}, #5cf2a0)` }}>
                  <Check className="h-5 w-5" />
                </motion.span>
                <p className="pt-2 text-lg text-white/85">{state === "already" ? w.already : w.pending}</p>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} noValidate data-testid="waitlist-form" exit={{ opacity: 0 }} className="flex flex-col gap-3 rounded-[28px] border border-white/15 bg-[#05070f]/60 p-2 backdrop-blur-xl sm:flex-row sm:rounded-full">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={w.placeholder}
                  aria-label={w.placeholder}
                  autoComplete="email"
                  data-testid="waitlist-email-input"
                  className="min-w-0 flex-1 bg-transparent px-5 py-3 text-base text-white outline-none placeholder:text-white/30"
                />
                <button type="submit" disabled={state === "sending"} data-testid="waitlist-submit-button" className="btn-primary group justify-center disabled:opacity-60">
                  {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {state === "sending" ? w.sending : w.cta}
                  {state !== "sending" && <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Reveal>
  );
};
